// modal-purchase.tsx

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { modalService } from './modal-purchase-service';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './modal-purchase/style.module.scss';
import { UIButton } from 'shared/ui';
import { ROUTES, TOKEN_ICONS, BOOSTER_ICONS } from 'shared/constants';

// Import your assets
import BobeCoin from 'assets/images/bobe-coin.png';
import QuantityIcon from 'assets/icons/quantity.png';
import EnergyCapacity from 'assets/images/energy-capacity.webp';

type ModalContextType = {
  showPurchaseSuccess: (item: any) => void;
  hidePurchaseSuccess: () => void;
};

const ModalContext = createContext<ModalContextType>({
  showPurchaseSuccess: () => {},
  hidePurchaseSuccess: () => {},
});

export const useModal = () => useContext(ModalContext);

type ModalProviderProps = {
  children: ReactNode;
};

export const PurchaseModalProvider = ({ children }: ModalProviderProps) => {
  const [purchaseSuccess, setPurchaseSuccess] = useState({
    isOpen: false,
    item: null as any,
  });

  const showPurchaseSuccess = (item: any) => {
    setPurchaseSuccess({
      isOpen: true,
      item,
    });
  };

  const hidePurchaseSuccess = () => {
    setPurchaseSuccess({
      isOpen: false,
      item: null,
    });
  };

  useEffect(() => {
    modalService.init(showPurchaseSuccess, hidePurchaseSuccess);
  }, []);

  return (
    <ModalContext.Provider value={{ showPurchaseSuccess, hidePurchaseSuccess }}>
      {children}
      {purchaseSuccess.isOpen && purchaseSuccess.item && (
        <PurchaseSuccessModal item={purchaseSuccess.item} onClose={hidePurchaseSuccess} />
      )}
    </ModalContext.Provider>
  );
};

const PurchaseSuccessModal = ({ item, onClose }: { item: any; onClose: () => void }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;

  const itemName = item.properties.name?.[currentLanguage] || item.properties.name?.en || '';

  // Get modal texts based on item type
  const getModalTexts = () => {
    switch (item.type) {
      case 'skin_hero':
        return {
          title: t('v2.market.modal.hero_title'),
          subtitle: t('v2.market.modal.hero_subtitle'),
          footer: t('v2.market.modal.hero_footer'),
        };
      case 'skin_oven':
        return {
          title: t('v2.market.modal.oven_title'),
          subtitle: t('v2.market.modal.oven_subtitle'),
          footer: t('v2.market.modal.oven_footer'),
        };
      case 'chest':
        return {
          title: t('v2.market.modal.chest_title'),
          subtitle: t('v2.market.modal.chest_subtitle'),
          footer: t('v2.market.modal.chest_footer'),
        };
      default:
        return {
          title: t('v2.market.modal.hero_title'),
          subtitle: t('v2.market.modal.hero_subtitle'),
          footer: '',
        };
    }
  };

  const modalTexts = getModalTexts();

  // Render appropriate content based on item type
  const renderItemContent = () => {
    switch (item.type) {
      case 'skin_hero':
        return renderHeroContent();
      case 'skin_oven':
        return renderOvenContent();
      case 'chest':
        return renderChestContent();
      default:
        return <div>{'OK'}</div>;
    }
  };

  const renderHeroContent = () => {
    const {
      level,
      properties: {
        energy_charges = 0,
        daily_limit_bonus = 0,
        session_bonus_percent = 0,
        boosters = [],
      },
    } = item;

    // Map booster codes to BOOSTER_ICONS keys
    const getBoosterIcon = (code: string) => {
      return BOOSTER_ICONS[code] || BOOSTER_ICONS.booster_insects_x; // fallback
    };

    return (
      <div className={styles.itemContent}>
        {/* Hero Image */}
        <div className={styles.heroImageSection}>
          <img src={`heroes_market/${level}.webp`} alt={itemName} className={styles.heroImage} />
        </div>

        {/* Main Stats Section */}
        <div className={styles.mainStats}>
          <div className={styles.chargesStat}>
            <span className={styles.chargesValue}>+{energy_charges}</span>
            <img src={TOKEN_ICONS.energyCharge} alt="" className={styles.chargesIcon} />
          </div>
        </div>

        {/* Bonus Text Lines */}
        <div className={styles.bonusLines}>
          <div className={styles.bonusLine}>
            <span className={styles.bonusPlus}>
              + {session_bonus_percent}% {t('v2.market.modal.bonus_session_1')}
            </span>
            <span className={styles.bonusText}> {t('v2.market.modal.bonus_session_2')}</span>
          </div>
          <div className={styles.bonusLine}>
            <span className={styles.bonusPlus}>+ {daily_limit_bonus}</span>
            <span className={styles.bonusText}> {t('v2.market.modal.bonus_daily_limit')}</span>
          </div>
        </div>

        {/* Boosters Section */}
        {boosters && boosters.length > 0 && (
          <div className={styles.boosterSection}>
            <div className={styles.boosterRow}>
              {boosters.map((booster: any, index: number) => (
                <div key={index} className={styles.boosterItem}>
                  <img
                    src={getBoosterIcon(booster.code)}
                    alt={booster.code}
                    className={styles.boosterIcon}
                  />
                  <div
                    className={styles.boosterQuantity}
                    style={{ backgroundImage: `url(${QuantityIcon})` }}
                  >
                    ×{booster.quantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Text */}
        {modalTexts.footer && (
          <div className={styles.footerText}>
            <p>{modalTexts.footer}</p>
          </div>
        )}
      </div>
    );
  };

  const renderOvenContent = () => {
    const {
      level,
      properties: { energy_capacity = 0, energy_charges = 0, exchange_discount = 0 },
    } = item;

    return (
      <div className={styles.itemContent}>
        {/* Oven Image */}
        <div className={styles.heroImageSection}>
          <img src={`ovens_market/${level}.webp`} alt={itemName} className={styles.heroImage} />
        </div>

        {/* Main Stats Section */}
        <div className={styles.mainStats}>
          <div className={styles.chargesStat}>
            <span className={styles.chargesValue}>+{energy_capacity}</span>
            <img src={EnergyCapacity} alt="" className={styles.chargesIcon} />
          </div>
          <div className={styles.chargesStat}>
            <span className={styles.chargesValue}>+{energy_charges}</span>
            <img src={TOKEN_ICONS.energyCharge} alt="" className={styles.chargesIcon} />
          </div>
        </div>

        {/* Exchange discount bonus */}
        {exchange_discount > 0 && (
          <div className={styles.bonusLines}>
            <div className={styles.bonusLine}>
              <span className={styles.bonusPlus}>
                + {t('v2.market.card.oven_exchange_discount')} {exchange_discount}%{' '}
              </span>
              <span className={styles.bonusText}>
                {' '}
                {t('v2.market.card.oven_exchange_discount_text')}
              </span>
            </div>
          </div>
        )}

        {/* Footer Text */}
        {modalTexts.footer && (
          <div className={styles.footerText}>
            <p>{modalTexts.footer}</p>
          </div>
        )}
      </div>
    );
  };

  const renderChestContent = () => {
    const {
      level,
      properties: { tokens = 0, bonus_tokens = 0, energy_charges = 0, contents = [] },
    } = item;

    return (
      <div className={styles.itemContent}>
        {/* Chest Image */}
        <div className={styles.heroImageSection}>
          <img src={`chests_market/${level}.webp`} alt={itemName} className={styles.heroImage} />
        </div>

        {/* Main Stats Section */}
        <div className={styles.mainStats}>
          <div className={styles.chargesStat}>
            <span className={styles.chargesValue}>+{energy_charges}</span>
            <img src={TOKEN_ICONS.energyCharge} alt="" className={styles.chargesIcon} />
          </div>
          <div className={styles.tokensStat}>
            <span className={styles.chargesValue}>+{tokens}</span>
            <img src={BobeCoin} alt="" className={styles.chargesIcon} />
          </div>
        </div>

        {/* Bonus info */}
        {bonus_tokens > 0 && (
          <div className={styles.bonusLines}>
            <div className={styles.bonusLine}>
              <span className={styles.bonusPlus}>
                +{bonus_tokens} {t('v2.market.card.token_bonus_1')}
              </span>
              <span className={styles.bonusText}> {t('v2.market.modal.chest_bonus_text')}</span>
            </div>
          </div>
        )}

        {/* Hero skins content - displayed like boosters in hero card */}
        {contents && contents.length > 0 && (
          <div className={styles.boosterSection}>
            <div className={styles.boosterRow}>
              {contents.slice(0, 8).map((content: any, index: number) => (
                <div key={index} className={styles.boosterItem}>
                  {content && content.skin_hero_level ? (
                    <>
                      <img
                        src={`heroes/${content.skin_hero_level}.webp`}
                        alt={`Hero skin level ${content.skin_hero_level}`}
                        className={styles.boosterIcon}
                      />
                      {content.quantity > 1 && (
                        <div
                          className={styles.boosterQuantity}
                          style={{ backgroundImage: `url(${QuantityIcon})` }}
                        >
                          ×{content.quantity}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.emptySlot}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Text */}
        {modalTexts.footer && (
          <div className={styles.footerText}>
            <p>{modalTexts.footer}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header Section */}
        <div className={styles.header}>
          <h2 className={styles.title}>{modalTexts.title}</h2>
          <p className={styles.subtitle}>{modalTexts.subtitle}</p>
        </div>

        {/* Card with gradient border */}
        <div className={styles.cardContainer}>
          {/* Pink Label */}
          <div className={styles.itemLabel}>
            <span>{itemName}</span>
          </div>

          {/* Content */}
          <div className={styles.contentWrapper}>{renderItemContent()}</div>
        </div>

        {/* Bottom Button */}
        <UIButton
          onClick={() => {
            onClose();
            navigate(ROUTES.BAKERY);
          }}
          className={styles.actionButton}
        >
          {t('v2.market.modal.go_to_bakery')}
        </UIButton>
      </div>
    </div>
  );
};
